import React from "react";
import { useGame } from "../api/api_hooks";
import PlayButton from "./PlayButton";
import { formatNumber, t } from "../i18n";

export default function GameDetailsDialog({ game, gameId, onClose, onPlay, onBuy, onSubscribe, isBuying, purchasesEnabled = true, onRemoteStateChange }) {
  const id = game?.id ?? gameId;
  const { data: full, isPending, isError } = useGame(id);

  const data = React.useMemo(() => full || game || {}, [full, game]);
  const {
    title = "",
    description,
    cover_image: coverImage,
    cover_video: coverVideo,
    gameplay_video: gameplayVideo,
    min_players: minPlayers,
    max_players: maxPlayers,
    playtime_estimate: playtimeEstimate,
    competition_type: competitionType,
    user_has_access: userHasAccess,
    difficulty,
    tags,
    price,
    video,
  } = data;

  const [index, setIndex] = React.useState(0);

  const mediaItems = React.useMemo(() => {
    const items = [];

    const addImages = (arr) => {
      if (!arr) return;
      const urls = Array.isArray(arr)
        ? arr
            .map((x) => (typeof x === "string" ? x : x?.url || x?.src || x?.image || x?.file))
            .filter(Boolean)
        : [];
      urls.forEach((u) => items.push({ type: "image", src: u }));
    };

    if (coverImage) items.push({ type: "image", src: coverImage });
    const vid = coverVideo || video;
    if (vid) items.push({ type: "image", src: vid });

    if (gameplayVideo) items.push({ type: "video", src: gameplayVideo });

    addImages(data.images || data.gallery || data.screenshots || data.media);

    const seen = new Set();
    return items.filter((m) => (seen.has(m.src) ? false : (seen.add(m.src), true)));
  }, [coverImage, coverVideo, video, gameplayVideo, data]);

  React.useEffect(() => {
    if (index >= mediaItems.length) setIndex(0);
  }, [mediaItems.length, index]);

  const hasMedia = mediaItems.length > 0;
  const prev = React.useCallback(() => setIndex((i) => (i - 1 + mediaItems.length) % mediaItems.length), [mediaItems.length]);
  const next = React.useCallback(() => setIndex((i) => (i + 1) % mediaItems.length), [mediaItems.length]);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose?.();
  };

  const handlePlay = React.useCallback(() => onPlay?.(data), [data, onPlay]);
  const handleBuy = () => onBuy?.(data);
  const handleSubscribe = React.useCallback(() => onSubscribe?.(data), [data, onSubscribe]);

  React.useEffect(() => {
    onRemoteStateChange?.(
      { index, mediaCount: mediaItems.length, title, userHasAccess: Boolean(userHasAccess) },
      { previous: prev, next, close: onClose, play: handlePlay, subscribe: handleSubscribe }
    );
  }, [handlePlay, handleSubscribe, index, mediaItems.length, next, onClose, onRemoteStateChange, prev, title, userHasAccess]);

  if (!id) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-labelledby="game-details-title"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-slate-950/95 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute end-4 top-4 z-10 rounded-full bg-white/5 px-3 py-1 text-xl font-semibold text-white transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          aria-label={t("Close details")}
        >
          <span aria-hidden>&times;</span>
        </button>

        <div className="grid gap-6 text-start md:grid-cols-[2fr,3fr]">
          <div className="relative min-h-[240px] bg-slate-900">
            {isPending && (
              <div className="absolute inset-0 grid place-items-center text-indigo-200/80">
                {t("Loading media...")}
              </div>
            )}
            {hasMedia ? (
              <div className="relative h-full w-full">
                {mediaItems[index].type === "image" ? (
                  <img src={mediaItems[index].src} alt={title} className="h-full w-full object-cover" />
                ) : (
                  <video
                    className="h-full w-full object-cover"
                    src={mediaItems[index].src}
                    poster={coverImage || undefined}
                    controls
                  />
                )}

                {mediaItems.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prev}
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-3 py-2 text-white backdrop-blur hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      aria-label={t("Previous")}
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={next}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-3 py-2 text-white backdrop-blur hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                      aria-label={t("Next")}
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                      {mediaItems.map((_, i) => (
                        <span
                          key={i}
                          onClick={() => setIndex(i)}
                          className={`h-1.5 w-6 cursor-pointer rounded-full ${i === index ? 'bg-white' : 'bg-white/40'}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="h-full w-full" />
            )}
          </div>

          <div className="space-y-5 p-6">
            <header className="space-y-2">
              <h2 id="game-details-title" className="text-2xl font-bold tracking-tight text-white">
                {title}
              </h2>
              <p className="text-sm text-indigo-200/80">
                {isPending && !description ? t("Loading description...") : description || t("No description available for this game yet.")}
              </p>
            </header>

            <dl className="grid grid-cols-2 gap-4 text-sm text-indigo-100/80">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300/80">{t("Players")}</dt>
                <dd className="mt-1 text-base text-white">
                  {formatNumber(minPlayers)} - {formatNumber(maxPlayers)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300/80">{t("Playtime")}</dt>
                <dd className="mt-1 text-base text-white">{t("~{{minutes}} minutes", { minutes: playtimeEstimate })}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300/80">{t("Difficulty")}</dt>
                <dd className="mt-1 text-base text-white">{t(difficulty)}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300/80">{t("Competition")}</dt>
                <dd className="mt-1 text-base text-white">{t(competitionType)}</dd>
              </div>
            </dl>

            {tags?.length ? (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-100/80"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            {!userHasAccess ? (
              <p className="rounded-2xl border border-yellow-300/25 bg-yellow-300/10 px-4 py-3 text-sm font-medium leading-6 text-yellow-100">
                {purchasesEnabled
                  ? t("Buy this game once and keep it forever, or subscribe monthly to unlock the full games library.")
                  : t("Online checkout is disabled during the beta. View the plans and ask an administrator to activate access.")}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-3 pt-2">
              <PlayButton
                userHasAccess={userHasAccess}
                onPlay={handlePlay}
                onBuy={handleBuy}
                onSubscribe={handleSubscribe}
                disabled={isError}
                isBuying={isBuying}
                purchasesEnabled={purchasesEnabled}
                price={price}
              />
              <button
                type="button"
                className="rounded-full border border-indigo-500/40 px-6 py-2 text-sm font-semibold uppercase tracking-[0.3em] text-indigo-100 transition hover:border-indigo-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                onClick={onClose}
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
